import VimeoPlayer from "@vimeo/player";
import { Icon } from "@iconify/vue2";
import VueSlider from "vue-slider-component";
import Cookies from "js-cookie";
import "vue-slider-component/theme/default.css";
import { resolveVimeoVideo } from "@/utils/vimeo";
import { interfaceT } from "@/utils/interfaceTranslationFallbacks";

export default {
  name: "video-player-vimeo",
  components: { Icon, VueSlider },
  props: {
    videoUrl: {
      type: String,
      required: true,
    },
    isKeepInViewportMode: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      isLoading: true,
      isBuffering: true,
      isPlaying: false,
      isPlayingIndicatorActive: false,
      isSeeking: false,
      isMoveActive: false,
      isMoveBackward: false,
      isMoveForward: false,
      isVideoStarted: false,
      isInViewport: true,
      observer: null,
      player: null,
      videoId: "",
      videoHash: "",
      loadError: false,
      cookieData: undefined,
      thumbnailUrl: "",
      volume: 1,
      isFullscreen: false,
      fullscreenWidth: window.innerWidth,
      fullscreenHeight: window.innerHeight,
      currentTime: 0,
      totalTime: 0,
      availableQualities: [],
      selectedQuality: "",
      availableSpeeds: [0.5, 0.75, 1, 1.1, 1.25, 1.37, 1.5, 1.75, 2],
      selectedSpeed: 1,
      isSpaceHeld: false,
      spaceHoldTimer: null,
      previousSpeed: 1,
      touchHoldTimer: null,
      isTouchHolding: false,
      mouseHoldTimer: null,
      isMouseHolding: false,
      skipNextClick: false,
      lastRightTapTime: 0,
      singleTapTimer: null,
      lastLeftTapTime: 0,
      singleLeftTapTimer: null,
      navigatorHideTime: 2000,
      isNavigationActive: false,
      navigationTimer: null,
      bufferTimer: null,
      moveTimer: null,
      isVolumeChanging: false,
      isVolumeActive: false,
      volumeChangingTimer: null,
      subtitleTracks: [],
      selectedSubtitle: "",
    };
  },
  computed: {
    formattedCurrentTime() {
      return this.formatTime(this.currentTime);
    },
    formattedTotalTime() {
      return this.formatTime(this.totalTime);
    },
    formattedContinueTime() {
      if (!this.cookieData?.currentTime) return;
      return this.formatTime(this.cookieData.currentTime);
    },
    isIos() {
      return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    },
    isWindows() {
      return /Windows/i.test(navigator.userAgent);
    },
    isAndroid() {
      return /Android/i.test(navigator.userAgent);
    },
    fullscreenStyle() {
      return {
        width: `${this.fullscreenWidth}px`,
        height: `${this.fullscreenHeight}px`,
      };
    },
    isMobile() {
      return window.innerWidth > 980;
    },
    loadErrorMessage() {
      return interfaceT(this, "videoLinkUnavailable");
    },
  },
  methods: {
    // async getVimeoThumbnail() {
    //   try {
    //     const response = await fetch(
    //       `https://vimeo.com/api/v2/video/${this.videoId}.json`
    //     );
    //     const data = await response.json();
    //     this.thumbnailUrl = data[0].thumbnail_large;
    //   } catch (error) {
    //     console.error("Error fetching Vimeo thumbnail:", error);
    //   }
    // },
    initVimeoPlayer() {
      const options = {
        controls: false,
        background: false,
        dnt: true,
        byline: false,
        title: false,
        portrait: false,
        loop: false,
        pip: true,
      };

      // Unlisted videos need the privacy hash: the SDK only accepts it via
      // the url option (vimeo.com/<id>/<hash>); public videos use the id.
      if (this.videoHash) {
        options.url = `https://vimeo.com/${this.videoId}/${this.videoHash}`;
      } else {
        options.id = this.videoId;
      }

      this.player = new VimeoPlayer("vimeo-player", options);

      this.player.on("loaded", async () => {
        // this.changeVolume();
        this.player.setVolume(0.99);

        this.player.getQualities().then((qualities) => {
          this.availableQualities = qualities.map((q) => q.id);
          this.selectedQuality = this.availableQualities[0];
        });

        const tracks = await this.player.getTextTracks();
        if (tracks.length) {
          this.subtitleTracks = [...tracks, { language: "", label: "Off" }];
        }
        this.selectedSubtitle = "";
        await this.player.disableTextTrack();

        this.isPlaying ? this.playVideo() : this.pauseVideo();
        this.isLoading = false;
      });

      this.player.on("bufferstart", () => {
        if (this.bufferTimer) clearTimeout(this.bufferTimer);
        this.isBuffering = true;
        if (this.isPlaying) {
          this.bufferTimer = setTimeout(() => {
            if (this.isBuffering) this.player?.pause();
          }, 200);
        }
      });

      this.player.on("bufferend", () => {
        this.isBuffering = false;
        if (this.isPlaying) this.player?.play();
      });

      this.player.on("timeupdate", (data) => {
        if (!this.isSeeking) {
          this.currentTime = data.seconds;
          this.totalTime = Math.round(data.duration);
        }
      });

      this.player.on("play", () => {
        this.isPlaying = true;
      });

      this.player.on("pause", () => {
        this.isPlaying = false;
      });

      this.player.on("ended", () => {
        this.player.setCurrentTime(0);
        this.isPlaying = false;
      });

      this.player.setCurrentTime(this.currentTime);
    },
    resetVimeoPlayer() {
      if (this.player) {
        this.player.destroy().then(() => {
          this.initVimeoPlayer();
        });
      } else {
        this.initVimeoPlayer();
      }
    },
    timelineMove(value) {
      if (this.isMoveActive || !this.isPlaying) return;
      this.player.setCurrentTime(this.currentTime + value);
      this.isMoveActive = true;
      value > 0 ? (this.isMoveForward = true) : (this.isMoveBackward = true);
      setTimeout(() => {
        this.isMoveActive = false;
        value > 0
          ? (this.isMoveForward = false)
          : (this.isMoveBackward = false);
      }, 300);
    },
    moveForward() {
      this.timelineMove(10);
    },
    moveBackward() {
      this.timelineMove(-10);
    },
    indicatorMoveForward() {
      clearTimeout(this.moveTimer);
      if (!this.isPlaying) return this.playVideo();
      this.moveForward();
    },
    indicatorMoveBackward() {
      clearTimeout(this.moveTimer);
      if (!this.isPlaying) return this.playVideo();
      this.moveBackward();
    },
    handleArrowVolumeChange(strategy) {
      this.isVolumeChanging = true;
      clearTimeout(this.volumeChangingTimer);
      this.volumeChangingTimer = setTimeout(() => {
        this.isVolumeChanging = false;
      }, this.navigatorHideTime);

      if (strategy === "increment") {
        const newVolume = this.volume + 0.1;
        this.volume = newVolume > 1 ? 1 : newVolume;
      } else if (strategy === "decrement") {
        const newVolume = this.volume - 0.1;
        this.volume = newVolume < 0 ? 0 : newVolume;
      }
    },
    isEditableFocused() {
      const el = document.activeElement;
      if (!el) return false;
      const tag = el.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || el.contentEditable === 'true';
    },
    handleKeyPress(event) {
      if (this.isEditableFocused()) return;

      if (event.code === "Space" && this.isInViewport && !event.repeat) {
        event.preventDefault();
        this.spaceHoldTimer = setTimeout(() => {
          this.isSpaceHeld = true;
          this.enableTemp2xSpeed();
        }, 300);
      } else if (event.code === "Space" && event.repeat) {
        event.preventDefault();
      }

      if (event.code === "Escape") {
        this.isFullscreen = false;
        document.body.classList.remove("no-scroll");
      }

      if (event.code === "ArrowRight") {
        event.preventDefault();
        this.moveForward();
      } else if (event.code === "ArrowLeft") {
        event.preventDefault();
        this.moveBackward();
      }

      if (event.code === "ArrowUp") {
        event.preventDefault();
        this.handleArrowVolumeChange("increment");
      } else if (event.code === "ArrowDown") {
        event.preventDefault();
        this.handleArrowVolumeChange("decrement");
      }
    },
    showPlayIndicator() {
      this.isPlayingIndicatorActive = true;
      setTimeout(() => {
        this.isPlayingIndicatorActive = false;
      }, 300);
    },
    async playVideo(currentTime) {
      if (!this.player || this.isPlaying) return;

      // fix for ios muted on start
      if (!this.isVideoStarted) {
        if (currentTime) {
          this.player.setCurrentTime(currentTime);
        } else {
          this.player.setCurrentTime(1);
          this.player.setCurrentTime(0);
        }
      }

      try {
        await this.player.setMuted(false);
        await this.player.setVolume(this.volume || 1);
        await this.player.play();
        this.isVideoStarted = true;
        this.showPlayIndicator();
      } catch (e) {
        console.error("Video play error:", e);
      }
    },
    pauseVideo() {
      if (!this.player || !this.isPlaying) return;
      this.player.pause();
      // this.isPlaying = false;
      this.showPlayIndicator();
    },
    toggleVideo() {
      clearTimeout(this.moveTimer);
      this.moveTimer = setTimeout(() => {
        this.isPlaying ? this.pauseVideo() : this.playVideo();
      }, 200);
    },
    continueVideo() {
      this.playVideo(this.cookieData?.currentTime ? this.player.setCurrentTime(this.cookieData.currentTime) : undefined);
    },
    toggleVolume() {
      this.volume = this.volume === 0 ? 1 : 0;
    },
    toggleVolumeModal() {
      this.isVolumeActive = !this.isVolumeActive;
    },
    hideVolumeModal() {
      this.isVolumeActive = false;
    },
    goFullscreen() {
      const videoElement = this.$refs.videoContainer;
      if (videoElement.requestFullscreen) {
        videoElement.requestFullscreen();
      } else if (videoElement.mozRequestFullScreen) {
        videoElement.mozRequestFullScreen();
      } else if (videoElement.webkitRequestFullscreen) {
        videoElement.webkitRequestFullscreen();
      } else if (videoElement.msRequestFullscreen) {
        videoElement.msRequestFullscreen();
      } else if (videoElement.webkitEnterFullScreen) {
        videoElement.webkitEnterFullScreen();
      }
    },
    exitFullscreen() {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    },
    toggleFullscreen() {
      if (this.isIos) {
        return this.player.requestFullscreen();
      }

      if (document.fullscreenElement) {
        this.exitFullscreen();
      } else {
        this.goFullscreen();
      }
    },
    updateFullscreenSize() {
      setTimeout(() => {
        this.fullscreenWidth = window.innerWidth;
        this.fullscreenHeight = window.innerHeight;
      }, 100);
    },
    changeVolume() {
      if (this.player) this.player.setVolume(this.volume);
    },
    startSeeking() {
      this.isSeeking = true;
    },
    changeProgress() {
      this.player
        .setCurrentTime(this.currentTime)
        .then(() => (this.isSeeking = false));
    },
    formatTime(seconds) {
      const minutes = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${String(minutes).padStart(2, "0")}:${String(secs).padStart(
        2,
        "0"
      )}`;
    },
    changeQuality() {
      if (this.player) this.player.setQuality(this.selectedQuality);
    },
    changeSpeed() {
      if (this.player) this.player.setPlaybackRate(this.selectedSpeed);
    },
    handleKeyUp(event) {
      if (this.isEditableFocused()) return;
      if (event.code === "Space") {
        event.preventDefault();
        clearTimeout(this.spaceHoldTimer);
        if (this.isSpaceHeld) {
          this.isSpaceHeld = false;
          this.disableTemp2xSpeed();
        } else {
          this.toggleVideo();
        }
      }
    },
    handleLeftTouchStart(event) {
      event.preventDefault();
    },
    handleLeftTouchEnd() {
      const now = Date.now();
      if (now - this.lastLeftTapTime < 300) {
        clearTimeout(this.singleLeftTapTimer);
        this.lastLeftTapTime = 0;
        this.indicatorMoveBackward();
      } else {
        this.singleLeftTapTimer = setTimeout(() => {
          this.toggleVideo();
        }, 250);
      }
      this.lastLeftTapTime = now;
    },
    handleRightTouchStart(event) {
      event.preventDefault();
      this.touchHoldTimer = setTimeout(() => {
        this.isTouchHolding = true;
        this.enableTemp2xSpeed();
      }, 300);
    },
    handleRightTouchEnd() {
      clearTimeout(this.touchHoldTimer);
      if (this.isTouchHolding) {
        this.isTouchHolding = false;
        this.disableTemp2xSpeed();
        return;
      }
      const now = Date.now();
      if (now - this.lastRightTapTime < 300) {
        clearTimeout(this.singleTapTimer);
        this.lastRightTapTime = 0;
        this.indicatorMoveForward();
      } else {
        this.singleTapTimer = setTimeout(() => {
          this.toggleVideo();
        }, 250);
      }
      this.lastRightTapTime = now;
    },
    handleRightMouseDown(event) {
      if (event.button !== 0) return;
      this.mouseHoldTimer = setTimeout(() => {
        this.isMouseHolding = true;
        this.enableTemp2xSpeed();
      }, 300);
    },
    handleRightMouseUp() {
      clearTimeout(this.mouseHoldTimer);
      if (this.isMouseHolding) {
        this.isMouseHolding = false;
        this.skipNextClick = true;
        this.disableTemp2xSpeed();
      }
    },
    handleRightClick() {
      if (this.skipNextClick) {
        this.skipNextClick = false;
        return;
      }
      this.toggleVideo();
    },
    enableTemp2xSpeed() {
      if (this.selectedSpeed !== 2) {
        this.previousSpeed = this.selectedSpeed;
        this.selectedSpeed = 2;
      }
    },
    disableTemp2xSpeed() {
      if (this.previousSpeed) {
        this.selectedSpeed = this.previousSpeed;
      }
    },
    handleNavigationShow() {
      if (this.navigationTimer) clearTimeout(this.navigationTimer);

      this.isNavigationActive = true;
      this.navigationTimer = setTimeout(() => {
        this.isNavigationActive = false;
      }, this.navigatorHideTime);
    },
    async changeSubtitle() {
      if (!this.player) return;
      if (this.selectedSubtitle) {
        await this.player.enableTextTrack(this.selectedSubtitle);
      } else {
        await this.player.disableTextTrack();
      }
    },
    async togglePiP() {
      if (!this.player) return;
      try {
        await this.player.requestPictureInPicture();
      } catch (error) {
        console.error("Error PiP Vimeo:", error);
      }
    },
  },
  async mounted() {
    // Supports both the old numeric links (vimeo.com/123) and the new share
    // links (vimeo.com/share/<UUID>, resolved through the oEmbed API).
    const resolved = await resolveVimeoVideo(this.videoUrl);

    if (resolved) {
      this.videoId = resolved.id;
      this.videoHash = resolved.hash || "";
      // this.getVimeoThumbnail();
      this.initVimeoPlayer();
      const cookieDataRaw = Cookies.get(`video-${this.videoId}`);
      if (cookieDataRaw) this.cookieData = JSON.parse(cookieDataRaw);
    } else {
      console.error("Unrecognized Vimeo link:", this.videoUrl);
      this.loadError = true;
      this.isLoading = false;
    }

    window.addEventListener("keydown", this.handleKeyPress);
    window.addEventListener("keyup", this.handleKeyUp);

    this.observer = new IntersectionObserver(
      ([entry]) => {
        this.isInViewport = entry.isIntersecting;
      },
      { threshold: 0.1 }
    );
    this.observer.observe(this.$el);

    this.updateFullscreenSize();
    window.addEventListener("resize", this.updateFullscreenSize);
    window.addEventListener("orientationchange", this.updateFullscreenSize);
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.handleKeyPress);
    window.removeEventListener("keyup", this.handleKeyUp);

    if (this.observer) this.observer.disconnect();

    window.removeEventListener("resize", this.updateFullscreenSize);
    window.removeEventListener("orientationchange", this.updateFullscreenSize);
  },
  watch: {
    volume() {
      this.changeVolume();
    },
    selectedQuality() {
      this.changeQuality();
    },
    selectedSpeed() {
      this.changeSpeed();
    },
    currentTime() {
      if (!this.videoId) return;
      const data = { currentTime: this.currentTime };
      Cookies.set(`video-${this.videoId}`, JSON.stringify(data), {
        expires: 90,
      });
    },
    async selectedSubtitle() {
      await this.changeSubtitle();
    },
  },
};
